<template>
  <b-modal
    id="duplicate-users-modal"
    ref="duplicate-users-modal"
    title="Duplicate Users"
    size="xl"
    cancel-variant="outline-secondary"
    ok-title="Close"
    cancel-title="Close"
    @hidden="resetModal"
  >
    <div class="d-flex justify-content-end mb-2">
      <b-button
        variant="primary"
        size="sm"
        @click="fetchDuplicateUsers"
        :disabled="loading"
      >
        <feather-icon icon="RefreshCwIcon" size="14" class="mr-1" />
        Refresh
      </b-button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner class="align-middle" />
      <p class="mt-2">Loading duplicate users...</p>
    </div>

    <!-- Error State -->
    <b-alert v-else-if="error" variant="danger" show dismissible @dismissed="error = null">
      <strong>Error:</strong> {{ error }}
    </b-alert>

    <!-- Tabs -->
    <b-tabs v-else content-class="mt-3">
      <!-- Duplicate Mobiles Tab -->
      <b-tab>
        <template #title>
          Duplicate Mobiles
          <b-badge v-if="mobilePagination.total > 0" variant="warning" class="ml-1">
            {{ mobilePagination.total }}
          </b-badge>
        </template>

        <!-- Empty State for Mobiles -->
        <div v-if="duplicateMobiles.length === 0 && !loading" class="text-center py-5">
          <p class="text-muted">No duplicate mobile numbers found.</p>
        </div>

        <!-- Duplicate Mobiles List -->
        <div v-else>
          <div
            v-for="(mobileGroup, index) in duplicateMobiles"
            :key="`mobile-${index}`"
            class="mb-4"
          >
            <b-card class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0">
                  <strong>Mobile:</strong> {{ mobileGroup.mobile }}
                </h6>
                <b-badge variant="warning">Count: {{ mobileGroup.count }}</b-badge>
              </div>
              <b-table
                :items="mobileGroup.users"
                :fields="userFields"
                responsive
                striped
                hover
                small
                class="mt-2"
              >
                <template #cell(is_active)="data">
                  <b-badge :variant="data.value === 1 ? 'success' : 'danger'">
                    {{ data.value === 1 ? 'Active' : 'Inactive' }}
                  </b-badge>
                </template>
                <template #cell(enterdatetime)="data">
                  {{ formatDate(data.value) }}
                </template>
                <template #cell(modifieddatetime)="data">
                  {{ formatDate(data.value) }}
                </template>
              </b-table>
            </b-card>
          </div>

          <!-- Pagination for Mobiles -->
          <div v-if="mobilePagination.total > 0" class="d-flex justify-content-between align-items-center flex-wrap mt-3">
            <div class="d-flex align-items-center mb-2">
              <span class="text-nowrap mr-2">Items per page:</span>
              <b-form-select
                v-model="mobilePageSize"
                :options="['10', '20', '50', '100']"
                size="sm"
                class="mr-2"
                style="width: 80px;"
                @input="handleMobilePageSizeChange"
              />
              <span class="text-nowrap">
                Showing {{ getMobileStartRange() }} to {{ getMobileEndRange() }} of {{ mobilePagination.total }}
              </span>
            </div>
            <div>
              <b-pagination
                v-model="mobilePageNum"
                :total-rows="mobilePagination.total"
                :per-page="parseInt(mobilePageSize)"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="handleMobilePageChange"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- Duplicate Emails Tab -->
      <b-tab>
        <template #title>
          Duplicate Emails
          <b-badge v-if="emailPagination.total > 0" variant="warning" class="ml-1">
            {{ emailPagination.total }}
          </b-badge>
        </template>

        <!-- Empty State for Emails -->
        <div v-if="duplicateEmails.length === 0 && !loading" class="text-center py-5">
          <p class="text-muted">No duplicate emails found.</p>
        </div>

        <!-- Duplicate Emails List -->
        <div v-else>
          <div
            v-for="(emailGroup, index) in duplicateEmails"
            :key="`email-${index}`"
            class="mb-4"
          >
            <b-card class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0">
                  <strong>Email:</strong> {{ emailGroup.email }}
                </h6>
                <b-badge variant="warning">Count: {{ emailGroup.count }}</b-badge>
              </div>
              <b-table
                :items="emailGroup.users"
                :fields="userFields"
                responsive
                striped
                hover
                small
                class="mt-2"
              >
                <template #cell(is_active)="data">
                  <b-badge :variant="data.value === 1 ? 'success' : 'danger'">
                    {{ data.value === 1 ? 'Active' : 'Inactive' }}
                  </b-badge>
                </template>
                <template #cell(enterdatetime)="data">
                  {{ formatDate(data.value) }}
                </template>
                <template #cell(modifieddatetime)="data">
                  {{ formatDate(data.value) }}
                </template>
              </b-table>
            </b-card>
          </div>

          <!-- Pagination for Emails -->
          <div v-if="emailPagination.total > 0" class="d-flex justify-content-between align-items-center flex-wrap mt-3">
            <div class="d-flex align-items-center mb-2">
              <span class="text-nowrap mr-2">Items per page:</span>
              <b-form-select
                v-model="emailPageSize"
                :options="['10', '20', '50', '100']"
                size="sm"
                class="mr-2"
                style="width: 80px;"
                @input="handleEmailPageSizeChange"
              />
              <span class="text-nowrap">
                Showing {{ getEmailStartRange() }} to {{ getEmailEndRange() }} of {{ emailPagination.total }}
              </span>
            </div>
            <div>
              <b-pagination
                v-model="emailPageNum"
                :total-rows="emailPagination.total"
                :per-page="parseInt(emailPageSize)"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="handleEmailPageChange"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </b-modal>
</template>

<script>
import {
  BModal,
  BTabs,
  BTab,
  BTable,
  BSpinner,
  BButton,
  BBadge,
  BCard,
  BAlert,
  BPagination,
  BFormSelect,
} from 'bootstrap-vue'
import axios from '@/components/axios'
import moment from 'moment'

const baseApi = process.env.VUE_APP_APIENDPOINT

export default {
  name: 'DuplicateUsers',
  components: {
    BModal,
    BTabs,
    BTab,
    BTable,
    BSpinner,
    BButton,
    BBadge,
    BCard,
    BAlert,
    BPagination,
    BFormSelect,
  },
  data() {
    return {
      loading: false,
      error: null,
      duplicateEmails: [],
      duplicateMobiles: [],
      emailPageNum: 1,
      emailPageSize: '20',
      mobilePageNum: 1,
      mobilePageSize: '20',
      emailPagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 0,
        has_next_page: false,
        has_previous_page: false,
      },
      mobilePagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 0,
        has_next_page: false,
        has_previous_page: false,
      },
      userFields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'mobile', label: 'Mobile', sortable: true },
        { key: 'role', label: 'Role', sortable: true },
        { key: 'username', label: 'Username', sortable: true },
        { key: 'fullname', label: 'Full Name', sortable: true },
        { key: 'is_active', label: 'Status', sortable: true },
        { key: 'enterdatetime', label: 'Created Date', sortable: true },
        { key: 'modifieddatetime', label: 'Modified Date', sortable: true },
      ],
    }
  },
  methods: {
    async fetchDuplicateUsers() {
      this.loading = true
      this.error = null

      try {
        const accessToken = localStorage.getItem('accessToken')
        const params = new URLSearchParams({
          page_num_email: this.emailPageNum.toString(),
          page_size_email: parseInt(this.emailPageSize).toString(),
          page_num_mobile: this.mobilePageNum.toString(),
          page_size_mobile: parseInt(this.mobilePageSize).toString(),
        })

        const requestOptions = {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          url: `${baseApi}/auth/duplicate-users?${params.toString()}`,
        }

        const response = await axios(requestOptions)

        if (response.data && response.data.status === 200) {
          // Handle new paginated response structure
          const emailsData = response.data.data.duplicate_emails || {}
          const mobilesData = response.data.data.duplicate_mobiles || {}

          this.duplicateEmails = emailsData.data || []
          this.emailPagination = emailsData.pagination || {
            current_page: 1,
            per_page: 20,
            total: 0,
            total_pages: 0,
            has_next_page: false,
            has_previous_page: false,
          }

          this.duplicateMobiles = mobilesData.data || []
          this.mobilePagination = mobilesData.pagination || {
            current_page: 1,
            per_page: 20,
            total: 0,
            total_pages: 0,
            has_next_page: false,
            has_previous_page: false,
          }

          // Sync page numbers with pagination response
          this.emailPageNum = this.emailPagination.current_page || 1
          this.mobilePageNum = this.mobilePagination.current_page || 1
        } else {
          this.error = response.data?.message || 'Failed to fetch duplicate users'
        }
      } catch (err) {
        console.error('Error fetching duplicate users:', err)
        this.error = err.response?.data?.message || err.message || 'An error occurred while fetching duplicate users'
      } finally {
        this.loading = false
      }
    },
    handleEmailPageChange(page) {
      this.emailPageNum = page
      this.fetchDuplicateUsers()
    },
    handleEmailPageSizeChange(size) {
      this.emailPageSize = size
      this.emailPageNum = 1
      this.fetchDuplicateUsers()
    },
    handleMobilePageChange(page) {
      this.mobilePageNum = page
      this.fetchDuplicateUsers()
    },
    handleMobilePageSizeChange(size) {
      this.mobilePageSize = size
      this.mobilePageNum = 1
      this.fetchDuplicateUsers()
    },
    getEmailStartRange() {
      if (this.emailPagination.total === 0) return 0
      return (this.emailPageNum - 1) * parseInt(this.emailPageSize) + 1
    },
    getEmailEndRange() {
      const end = this.emailPageNum * parseInt(this.emailPageSize)
      return end > this.emailPagination.total ? this.emailPagination.total : end
    },
    getMobileStartRange() {
      if (this.mobilePagination.total === 0) return 0
      return (this.mobilePageNum - 1) * parseInt(this.mobilePageSize) + 1
    },
    getMobileEndRange() {
      const end = this.mobilePageNum * parseInt(this.mobilePageSize)
      return end > this.mobilePagination.total ? this.mobilePagination.total : end
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      return moment(dateString).format('YYYY-MM-DD HH:mm:ss')
    },
    openModal() {
      this.$refs['duplicate-users-modal'].show()
      this.fetchDuplicateUsers()
    },
    closeModal() {
      this.$refs['duplicate-users-modal'].hide()
    },
    resetModal() {
      this.error = null
      this.duplicateEmails = []
      this.duplicateMobiles = []
      this.emailPageNum = 1
      this.emailPageSize = '20'
      this.mobilePageNum = 1
      this.mobilePageSize = '20'
      this.emailPagination = {
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 0,
        has_next_page: false,
        has_previous_page: false,
      }
      this.mobilePagination = {
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 0,
        has_next_page: false,
        has_previous_page: false,
      }
    },
  },
}
</script>

<style scoped>
.card {
  border: 1px solid #e0e0e0;
}
</style>

